import "./loading.sass";

/**
 * Componente de indicador de carga con accesibilidad completa
 * 
 * @description Proporciona feedback visual y accesible durante operaciones asíncronas.
 * Implementa las mejores prácticas de accesibilidad web para estados de carga,
 * garantizando que todos los usuarios reciban notificación apropiada del estado
 * de la aplicación sin interrumpir su experiencia de navegación.
 * 
 * **Características de accesibilidad:**
 * - `role="status"` identifica el elemento como indicador de estado
 * - `aria-live="polite"` anuncia cambios sin interrumpir lectores de pantalla
 * - `aria-hidden="true"` en indicador visual para evitar redundancia
 * - Cumple con guías WCAG 2.1 para elementos de estado
 * 
 * **Casos de uso:**
 * - Carga de resultados de búsqueda
 * - Peticiones a APIs externas
 * - Procesamiento de formularios
 * - Navegación entre páginas
 * - Cualquier operación asíncrona
 * 
 * **Diseño visual:**
 * - Centrado en pantalla para máxima visibilidad
 * - Indicador animado no intrusivo
 * - Compatible con temas y estilos personalizados
 * - Responsive para todos los dispositivos
 * - Mensaje personalizado opcional para contexto
 * 
 * @component
 * @param {Object} props - Props del componente
 * @param {string} [props.message] - Mensaje personalizado a mostrar durante la carga
 * 
 * @returns {JSX.Element} Indicador de carga accesible y centrado con mensaje opcional
 * @since 1.3.0
 * @author Valeria Garcia
 * 
 * @see {@link ../BuscadorApp.js} Componente principal que utiliza Loading
 * @see {@link ../results/results.js} Componente que se muestra después de Loading
 * @see {@link https://www.w3.org/WAI/ARIA/apg/patterns/alert/} Guía ARIA para elementos de estado
 * @see {@link https://www.w3.org/WAI/WCAG21/Understanding/status-messages.html} WCAG 2.1 - Mensajes de estado
 */

export default function Loading({ message = 'Cargando...' }) {
  return (
    <div className="loading" role="status" aria-live="polite">
      {message && <p className="loading-message">{message}</p>}
      <div className="container-loading">
        <div className="loadingIndicator" aria-hidden="true"></div>
      </div>
    </div>
  );
}